import type { ReactNode } from "react";
import { navigate, routes, useRoute, type Route } from "../../router";
import { Icon, type IconName } from "./Icon";
import { ExpertMark } from "./ExpertMark";
import type { ExpertSummary } from "../../api";

type NavItem = {
  id: string;
  label: string;
  path: string;
  icon: IconName;
  active: (route: Route) => boolean;
  go: () => void;
  /** When false, dims the nav item; clicking still attempts navigation. */
  enabled?: boolean;
};

const baseNav = (route: Route): NavItem[] => [
  {
    id: "picker",
    label: "New meeting",
    path: "/meetings/new",
    icon: "plus",
    active: (r) => r.kind === "meeting_new",
    go: () => navigate(routes.meetingNew()),
  },
  {
    id: "meetings",
    label: "Past meetings",
    path: "/meetings",
    icon: "list",
    active: (r) => r.kind === "meetings_list",
    go: () => navigate(routes.meetingsList()),
  },
  ...(route.kind === "meeting" ||
  route.kind === "transcript" ||
  route.kind === "frames"
    ? meetingNav(route.meetingId)
    : []),
];

const meetingNav = (id: string): NavItem[] => [
  {
    id: "meeting",
    label: "In meeting",
    path: `/meetings/${id}`,
    icon: "record",
    active: (r) => r.kind === "meeting" && r.meetingId === id,
    go: () => navigate(routes.meeting(id)),
  },
  {
    id: "transcript",
    label: "Transcript",
    path: `/meetings/${id}/transcript`,
    icon: "transcript",
    active: (r) => r.kind === "transcript" && r.meetingId === id,
    go: () => navigate(routes.transcript(id)),
  },
  {
    id: "frames",
    label: "Frames",
    path: `/meetings/${id}/frames`,
    icon: "frames",
    active: (r) => r.kind === "frames" && r.meetingId === id,
    go: () => navigate(routes.frames(id)),
  },
];

const settingsNav = (): NavItem[] => [
  {
    id: "settings",
    label: "Settings",
    path: "/settings",
    icon: "cog",
    active: (r) => r.kind === "settings",
    go: () => navigate(routes.settings()),
  },
];

const EDGE_KINDS: ReadonlyArray<{ kind: string; label: string }> = [
  { kind: "bot_blocked", label: "Bot blocked" },
  { kind: "recall_down", label: "Recall down" },
  { kind: "offline", label: "Offline" },
  { kind: "missing_calendar_permissions", label: "Missing calendar perms" },
  { kind: "finalize_failed", label: "Finalize failed" },
];

const edgeStatesNav = (): NavItem[] =>
  EDGE_KINDS.map(({ kind, label }) => ({
    id: `edge-${kind}`,
    label,
    path: `/edge/${kind}`,
    icon: "close",
    active: (r) => r.kind === "edge" && r.errorKind === kind,
    go: () => navigate(routes.edge(kind)),
  }));

const referenceNav = (): NavItem[] => [
  {
    id: "design",
    label: "Design system",
    path: "/design-system",
    icon: "grid",
    active: (r) => r.kind === "design",
    go: () => navigate("/design"),
  },
];

/** Edge states group is dev-gated. Visible only when:
 *   1) the current URL has `?dev=1` set, OR
 *   2) the user's identity has `is_staff: true` (future api flag — for
 *      now, anyone with a recspec.dev or anthropic.com email).
 */
function shouldShowEdgeStates(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.get("dev") === "1";
}

export function Sidebar({
  footer,
  configCard,
  collapsed = false,
  onToggle,
  onClose,
}: {
  /** Optional footer content (e.g. user identity + sign out). */
  footer?: ReactNode;
  /** Optional bottom config card (e.g. current meeting summary). */
  configCard?: ReactNode;
  /** When true, render the icon-only collapsed variant. */
  collapsed?: boolean;
  /** Toggle handler for the collapse/expand button. */
  onToggle?: () => void;
  /** Mobile-only close handler. When provided, the drawer header renders
   *  a close button (CSS-hidden above the mobile breakpoint), and any
   *  nav-item selection invokes this to auto-close the drawer. */
  onClose?: () => void;
}) {
  const route = useRoute();
  const wrap = (items: NavItem[]): NavItem[] =>
    onClose
      ? items.map((item) => ({
          ...item,
          go: () => {
            item.go();
            onClose();
          },
        }))
      : items;
  const nav = wrap(baseNav(route));
  const settings = wrap(settingsNav());
  const edge = wrap(shouldShowEdgeStates() ? edgeStatesNav() : []);
  const ref = wrap(referenceNav());

  return (
    <aside
      className="recspec-sidebar"
      style={{
        borderRight: "1px solid var(--rule)",
        background: "var(--paper-2)",
        display: "flex",
        flexDirection: "column",
        padding: collapsed ? "12px 8px" : "18px 14px",
        overflow: "auto",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: collapsed ? "0" : "4px 8px",
          marginBottom: 14,
          justifyContent: collapsed ? "center" : "flex-start",
        }}
      >
        <button
          type="button"
          onClick={() => navigate(routes.home())}
          aria-label="Home"
          style={{
            width: 26,
            height: 26,
            borderRadius: 5,
            background: "var(--ink)",
            color: "var(--paper)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "var(--mono)",
            fontSize: 13,
            fontWeight: 700,
            border: 0,
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          R
        </button>
        {!collapsed && (
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 14, lineHeight: 1 }}>
              RecSpec
            </div>
            <div className="mono dim" style={{ fontSize: 10, marginTop: 2 }}>
              alpha.recspec.dev
            </div>
          </div>
        )}
        {onToggle && (
          <button
            type="button"
            onClick={onToggle}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="btn ghost icon recspec-sidebar-toggle"
            style={{
              padding: 4,
              border: 0,
              background: "transparent",
              cursor: "pointer",
              flexShrink: 0,
              ...(collapsed ? { marginTop: 6 } : {}),
            }}
          >
            <Icon
              name={collapsed ? "chevR" : "chevL"}
              size={14}
              color="var(--ink-3)"
            />
          </button>
        )}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="recspec-sidebar-close"
            style={{
              background: "transparent",
              border: 0,
              padding: 6,
              cursor: "pointer",
              color: "var(--ink-2)",
              flexShrink: 0,
            }}
          >
            <Icon name="close" size={14} />
          </button>
        )}
      </div>

      <NavGroup eyebrow="Workspace" items={nav} collapsed={collapsed} />

      <NavGroup eyebrow="Account" items={settings} collapsed={collapsed} />

      {edge.length > 0 && (
        <NavGroup eyebrow="Edge states" items={edge} collapsed={collapsed} />
      )}

      <NavGroup eyebrow="Reference" items={ref} collapsed={collapsed} />

      {!collapsed && (
        <div style={{ marginTop: "auto" }}>
          {configCard}
          {footer && (
            <div
              style={{
                paddingTop: 12,
                marginTop: 12,
                borderTop: "1px solid var(--rule)",
              }}
            >
              {footer}
            </div>
          )}
        </div>
      )}
    </aside>
  );
}

function NavGroup({
  eyebrow,
  items,
  collapsed,
}: {
  eyebrow: string;
  items: NavItem[];
  collapsed: boolean;
}) {
  if (items.length === 0) return null;
  return (
    <>
      {!collapsed && (
        <div className="eyebrow" style={{ padding: "4px 8px 6px" }}>
          {eyebrow}
        </div>
      )}
      <nav
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          marginBottom: 14,
        }}
      >
        {items.map((item) => (
          <NavButton key={item.id} item={item} collapsed={collapsed} />
        ))}
      </nav>
    </>
  );
}

function NavButton({
  item,
  collapsed,
}: {
  item: NavItem;
  collapsed: boolean;
}) {
  const route = useRoute();
  const active = item.active(route);
  const disabled = item.enabled === false;
  return (
    <button
      type="button"
      onClick={() => !disabled && item.go()}
      disabled={disabled}
      aria-label={item.label}
      title={collapsed ? item.label : undefined}
      style={{
        display: "flex",
        alignItems: "center",
        gap: collapsed ? 0 : 10,
        justifyContent: collapsed ? "center" : "flex-start",
        width: "100%",
        textAlign: "left",
        padding: collapsed ? "8px 0" : "8px 10px",
        borderRadius: 6,
        border: 0,
        background: active ? "var(--card)" : "transparent",
        cursor: disabled ? "not-allowed" : "pointer",
        boxShadow: active ? "var(--sh-1)" : "none",
        opacity: disabled ? 0.45 : 1,
      }}
    >
      <Icon
        name={item.icon}
        size={14}
        color={active ? "var(--ink)" : "var(--ink-3)"}
      />
      {!collapsed && (
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: active ? 600 : 500,
              color: active ? "var(--ink)" : "var(--ink-2)",
            }}
          >
            {item.label}
          </div>
          <div
            className="mono"
            style={{
              fontSize: 10,
              color: "var(--ink-3)",
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {item.path}
          </div>
        </div>
      )}
    </button>
  );
}

/** Bottom card showing current meeting configuration. Render inside
 *  Sidebar's `configCard` slot when on a meeting route. */
export function CurrentConfigCard({
  language,
  tone,
  voice,
  picked,
}: {
  language: string;
  tone: string;
  voice: string;
  picked: ReadonlyArray<
    Pick<ExpertSummary, "monogram" | "color_token" | "display_name">
  >;
}) {
  return (
    <>
      <div className="eyebrow" style={{ padding: "8px" }}>
        Current configuration
      </div>
      <div className="card" style={{ padding: 12 }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            fontSize: 11.5,
          }}
        >
          <Row k="Language" v={<span className="mono">{language}</span>} />
          <Row
            k="Tone"
            v={<span style={{ textTransform: "capitalize" }}>{tone}</span>}
          />
          <Row
            k="Voice"
            v={<span style={{ textTransform: "capitalize" }}>{voice}</span>}
          />
        </div>
        {picked.length > 0 && (
          <div style={{ display: "flex", marginTop: 10, gap: 4 }}>
            {picked.map((e) => (
              <ExpertMark key={e.monogram} expert={e} size={22} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function Row({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <span className="dim">{k}</span>
      <span style={{ fontWeight: 500 }}>{v}</span>
    </div>
  );
}
